import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ordersAPI, checklistAPI, customersAPI, vehiclesAPI, servicesAPI, employeesAPI } from '../../services/api';
import toast from 'react-hot-toast';

const CL_PARTS = [
  { key: 'scratches',  label: 'Arranhões' },
  { key: 'dents',      label: 'Amassados' },
  { key: 'windows',    label: 'Vidros' },
  { key: 'wheels',     label: 'Rodas/Calotas' },
  { key: 'tires',      label: 'Pneus' },
  { key: 'seats',      label: 'Bancos/Interior' },
  { key: 'dashboard',  label: 'Painel' },
  { key: 'headlights', label: 'Faróis' },
  { key: 'taillights', label: 'Lanternas' },
  { key: 'mirrors',    label: 'Espelhos' },
  { key: 'paint',      label: 'Pintura' },
];

const EMPTY_CL = Object.fromEntries(CL_PARTS.map(p => [p.key, '']));
EMPTY_CL.general_notes = '';
EMPTY_CL.customer_signature = '';

const STATUS_MAP = {
  waiting:         { bg:'#FEF3C7', color:'#92400E', label:'Aguardando' },
  in_progress:     { bg:'#DBEAFE', color:'#1D4ED8', label:'Em andamento' },
  waiting_payment: { bg:'#FEF3C7', color:'#B45309', label:'Ag. Pagamento' },
  done:            { bg:'#D1FAE5', color:'#065F46', label:'Finalizado' },
  delivered:       { bg:'#D1FAE5', color:'#065F46', label:'Entregue' },
  cancelled:       { bg:'#FEE2E2', color:'#991B1B', label:'Cancelado' },
};
const PAYMENT_METHODS = [
  {value:'cash',        label:'Dinheiro'},
  {value:'pix',         label:'PIX'},
  {value:'credit_card', label:'Cartão de Crédito'},
  {value:'debit_card',  label:'Cartão de Débito'},
  {value:'transfer',    label:'Transferência'},
  {value:'check',       label:'Cheque'},
  {value:'other',       label:'Outro'},
];
const PM_LEGACY = {dinheiro:'cash',cartao_credito:'credit_card',cartao_debito:'debit_card',transferencia:'transfer',cheque:'check',outro:'other'};
const normPM = v => PM_LEGACY[v] || v || 'cash';
const fmt = v => `R$ ${parseFloat(v||0).toFixed(2).replace('.',',')}`;
const inp = { width:'100%', padding:'9px 12px', border:'1.5px solid #E5E7EB', borderRadius:8, outline:'none', boxSizing:'border-box', fontSize:'.9rem' };
const lbl = { display:'block', fontWeight:600, fontSize:'.8rem', marginBottom:5, color:'#374151' };

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [order, setOrder]         = useState(null);
  const [loading, setLoading]     = useState(false);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles]   = useState([]);
  const [services, setServices]   = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm]           = useState({ customer_id:'', vehicle_id:'', employee_id:'', payment_method:'cash', notes:'', items:[] });
  const [checklist,    setChecklist]    = useState({ ...EMPTY_CL });
  const [clLoaded,     setClLoaded]     = useState(false);
  const [activeTab,    setActiveTab]    = useState('details');
  const [photoFile,    setPhotoFile]    = useState(null);

  useEffect(() => {
    customersAPI.list({ limit:200 }).then(r => setCustomers(r.data.data || []));
    servicesAPI.list().then(r => setServices(r.data.data || []));
    employeesAPI.list().then(r => setEmployees(r.data.data || []));
    if (!isNew) loadOrder();
  }, [id]);

  // Load checklist lazily when tab is first opened
  useEffect(() => {
    if (activeTab === 'checklist' && !isNew && !clLoaded) {
      checklistAPI.show(id)
        .then(r => {
          const cl = r.data.data;
          if (cl) {
            const state = { ...EMPTY_CL };
            CL_PARTS.forEach(p => { state[p.key] = cl[p.key] || ''; });
            state.general_notes      = cl.general_notes      || '';
            state.customer_signature = cl.customer_signature || '';
            setChecklist(state);
          }
          setClLoaded(true);
        })
        .catch(() => setClLoaded(true));
    }
  }, [activeTab, clLoaded, id, isNew]);

  useEffect(() => {
    if (form.customer_id) {
      vehiclesAPI.byCustomer(form.customer_id).then(r => setVehicles(r.data.data || []));
    } else if (!isNew && form.vehicle_id) {
      // O.S. convertida de agendamento: carrega o veículo individualmente para aparecer no select
      vehiclesAPI.show(form.vehicle_id)
        .then(r => { if (r.data.data) setVehicles([r.data.data]); })
        .catch(() => {});
    }
  }, [form.customer_id, form.vehicle_id]);

  const loadOrder = async () => {
    setLoading(true);
    try {
      const r = await ordersAPI.show(id);
      const o = r.data.data;
      setOrder(o);
      setForm({ customer_id: o.customer_id, vehicle_id: o.vehicle_id, employee_id: o.employee_id||'', payment_method: normPM(o.payment_method), notes: o.notes||'', items: (o.items || []).map(it => ({ ...it, price: it.unit_price })) });
      if (o.checklist) setChecklist({ observations: o.checklist.observations||'', damage_items: o.checklist.items || [] });
    } finally { setLoading(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { service_id:'', price:'', quantity:1 }] }));
  const setItem = (i, k, v) => setForm(f => { const items = [...f.items]; items[i] = { ...items[i], [k]: v }; return { ...f, items }; });
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_,j) => j!==i) }));

  const subtotal = form.items.reduce((acc, it) => acc + (parseFloat(it.price||0) * parseInt(it.quantity||1)), 0);

  const buildPayload = () => ({
    ...form,
    employee_id:    form.employee_id    || null,
    customer_id:    form.customer_id    || null,
    vehicle_id:     form.vehicle_id     || null,
    payment_method: form.payment_method || null,
    items: form.items.map(it => ({
      service_id:  it.service_id || null,
      description: services.find(s => String(s.id) === String(it.service_id))?.name || 'Serviço',
      unit_price:  parseFloat(it.price || 0),
      quantity:    parseInt(it.quantity || 1),
    })),
  });

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = buildPayload();
      if (isNew) {
        const r = await ordersAPI.create(payload);
        toast.success('O.S. criada!');
        navigate(`/orders/${r.data.data.id}`);
      } else {
        await ordersAPI.update(id, payload);
        toast.success('O.S. atualizada!'); loadOrder();
      }
    } catch (err) { toast.error(err.response?.data?.message || 'Erro.'); }
  };

  const changeStatus = async (status) => {
    try { await ordersAPI.updateStatus(id, { status }); toast.success('Status atualizado!'); loadOrder(); }
    catch (err) { toast.error(err.response?.data?.message || 'Erro.'); }
  };

  const saveChecklist = async () => {
    try { await checklistAPI.save(id, checklist); toast.success('Checklist salvo!'); setClLoaded(true); }
    catch { toast.error('Erro ao salvar checklist.'); }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return;
    const fd = new FormData(); fd.append('photo', photoFile);
    try { await ordersAPI.uploadPhoto(id, photoFile); toast.success('Foto enviada!'); setPhotoFile(null); loadOrder(); }
    catch { toast.error('Erro ao enviar foto.'); }
  };

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'#6B7280' }}>Carregando...</div>;

  const s = order ? (STATUS_MAP[order.status] || STATUS_MAP.waiting) : null;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
        <button onClick={()=>navigate('/orders')} style={{ background:'none', border:'none', cursor:'pointer', color:'#6B7280', fontSize:'1.2rem' }}>←</button>
        <h1 style={{ fontSize:'1.5rem', fontWeight:700, color:'#0F172A', margin:0 }}>
          {isNew ? 'Nova O.S.' : `O.S. #${order?.order_number}`}
        </h1>
        {s && <span style={{ background:s.bg, color:s.color, borderRadius:6, padding:'4px 12px', fontSize:'.8rem', fontWeight:700 }}>{s.label}</span>}
        {!isNew && (
          <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
            {['done','delivered'].includes(order?.status) && (
              <button
                onClick={async () => {
                  try {
                    const r = await ordersAPI.receiptLink(id);
                    const url = r.data.data.url;
                    window.open(url, '_blank');
                    if (navigator.clipboard) {
                      await navigator.clipboard.writeText(url);
                      toast.success('Link do recibo copiado!');
                    }
                  } catch { toast.error('Erro ao gerar recibo.'); }
                }}
                style={{ padding:'7px 16px', background:'#D1FAE5', border:'1.5px solid #6EE7B7', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:'.82rem', color:'#065F46' }}>
                Recibo do Cliente
              </button>
            )}
            <button
              onClick={() => window.open(`/api/orders/${id}/pdf?token=${localStorage.getItem('af_token')}`, '_blank')}
              style={{ padding:'7px 16px', background:'#F3F4F6', border:'1.5px solid #E5E7EB', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:'.82rem', color:'#374151' }}>
              Imprimir / PDF
            </button>
          </div>
        )}
      </div>

      {!isNew && order && (
        <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
          {Object.entries(STATUS_MAP).map(([st,{label,bg,color}]) => (
            <button key={st} onClick={()=>changeStatus(st)} disabled={order.status===st}
              style={{ background: order.status===st?bg:'#F8FAFC', color: order.status===st?color:'#6B7280', border:`1.5px solid ${order.status===st?color:'#E5E7EB'}`, borderRadius:8, padding:'6px 14px', cursor: order.status===st?'default':'pointer', fontWeight:600, fontSize:'.8rem' }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {!isNew && (
        <div style={{ display:'flex', gap:0, borderBottom:'2px solid #E5E7EB', marginBottom:20 }}>
          {['details','checklist','photos'].map(t => (
            <button key={t} onClick={()=>setActiveTab(t)}
              style={{ padding:'10px 20px', background:'none', border:'none', cursor:'pointer', fontWeight:700, fontSize:'.9rem',
                color: activeTab===t?'#2563EB':'#6B7280', borderBottom: activeTab===t?'2px solid #2563EB':'2px solid transparent', marginBottom:-2 }}>
              {t==='details'?'Detalhes':t==='checklist'?'Checklist':'Fotos'}
            </button>
          ))}
        </div>
      )}

      {(isNew || activeTab === 'details') && (
        <form onSubmit={save}>
          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:24, marginBottom:16 }}>
            <h3 style={{ fontWeight:700, marginBottom:16, color:'#0F172A' }}>Informações</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div>
                <label style={lbl}>Cliente *</label>
                <select value={form.customer_id} onChange={e=>set('customer_id',e.target.value)} required style={inp}>
                  <option value="">Selecione</option>
                  {customers.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Veículo *</label>
                <select value={form.vehicle_id} onChange={e=>set('vehicle_id',e.target.value)} required style={inp}>
                  <option value="">Selecione</option>
                  {vehicles.map(v=><option key={v.id} value={v.id}>{v.plate} — {v.model}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Responsável</label>
                <select value={form.employee_id} onChange={e=>set('employee_id',e.target.value)} style={inp}>
                  <option value="">Sem responsável</option>
                  {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Forma de pagamento</label>
                <select value={form.payment_method} onChange={e=>set('payment_method',e.target.value)} style={inp}>
                  {PAYMENT_METHODS.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Observações</label>
                <textarea value={form.notes} onChange={e=>set('notes',e.target.value)} rows={2} style={{ ...inp, resize:'vertical' }} />
              </div>
            </div>
          </div>

          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:24, marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <h3 style={{ fontWeight:700, color:'#0F172A', margin:0 }}>Itens / Serviços</h3>
              <button type="button" onClick={addItem} style={{ background:'#EFF6FF', color:'#2563EB', border:'none', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontWeight:600, fontSize:'.85rem' }}>+ Adicionar</button>
            </div>
            {form.items.length === 0 && <p style={{ color:'#9CA3AF', textAlign:'center', padding:'16px 0' }}>Nenhum serviço adicionado.</p>}
            {form.items.map((it, i) => (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr auto', gap:10, marginBottom:10, alignItems:'end' }}>
                <div>
                  {i===0 && <label style={lbl}>Serviço</label>}
                  <select value={it.service_id} onChange={e=>{
                    setItem(i,'service_id',e.target.value);
                    const svc = services.find(s=>String(s.id)===e.target.value);
                    if (svc) {
                      const veh = vehicles.find(v=>String(v.id)===String(form.vehicle_id));
                      const vtype = veh?.type || 'hatch';
                      const price = svc.prices?.[vtype] ?? svc.prices?.hatch ?? Object.values(svc.prices||{})[0] ?? '';
                      setItem(i,'price', price);
                    }
                  }} style={inp}>
                    <option value="">Selecione</option>
                    {services.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  {i===0 && <label style={lbl}>Preço</label>}
                  <input type="number" step="0.01" min="0" value={it.price} onChange={e=>setItem(i,'price',e.target.value)} placeholder="0.00" style={inp} />
                </div>
                <div>
                  {i===0 && <label style={lbl}>Qtd</label>}
                  <input type="number" min="1" value={it.quantity} onChange={e=>setItem(i,'quantity',e.target.value)} style={inp} />
                </div>
                <button type="button" onClick={()=>removeItem(i)} style={{ background:'#FEF2F2', color:'#DC2626', border:'none', borderRadius:8, padding:'9px 12px', cursor:'pointer', alignSelf:'flex-end' }}>✕</button>
              </div>
            ))}
            {form.items.length > 0 && (
              <div style={{ textAlign:'right', marginTop:12, fontWeight:700, fontSize:'1.1rem', color:'#059669' }}>
                Total: {fmt(subtotal)}
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button type="button" onClick={()=>navigate('/orders')} style={{ padding:'10px 20px', border:'1.5px solid #E5E7EB', borderRadius:8, background:'#fff', cursor:'pointer' }}>Cancelar</button>
            <button type="submit" style={{ padding:'10px 28px', background:'#2563EB', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer' }}>
              {isNew ? 'Criar O.S.' : 'Salvar alterações'}
            </button>
          </div>
        </form>
      )}

      {!isNew && activeTab === 'checklist' && (
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:24 }}>
          <h3 style={{ fontWeight:700, marginBottom:6, color:'#0F172A' }}>Checklist de Avarias</h3>
          <p style={{ fontSize:'.8rem', color:'#9CA3AF', marginBottom:18 }}>Marque as partes com avaria e descreva os danos observados.</p>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:12, marginBottom:20 }}>
            {CL_PARTS.map(({ key, label }) => {
              const val     = checklist[key] || '';
              const damaged = val !== '';
              return (
                <div key={key} style={{ border:`1.5px solid ${damaged ? '#EF4444' : '#E5E7EB'}`, borderRadius:10, padding:12, background: damaged ? '#FFF5F5' : '#FAFAFA' }}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom: damaged ? 8 : 0 }}>
                    <input type="checkbox" checked={damaged}
                      onChange={e => setChecklist(c => ({ ...c, [key]: e.target.checked ? 'yes' : '' }))} />
                    <span style={{ fontWeight:600, fontSize:'.85rem', color: damaged ? '#DC2626' : '#374151' }}>{label}</span>
                    {damaged && <span style={{ fontSize:'.7rem', color:'#DC2626', marginLeft:'auto' }}>⚠ Avaria</span>}
                  </label>
                  {damaged && (
                    <input
                      value={val === 'yes' ? '' : val}
                      onChange={e => setChecklist(c => ({ ...c, [key]: e.target.value || 'yes' }))}
                      placeholder="Descreva o dano (opcional)"
                      style={{ ...inp, fontSize:'.8rem', padding:'6px 10px' }} />
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Observações gerais</label>
            <textarea value={checklist.general_notes} onChange={e=>setChecklist(c=>({...c,general_notes:e.target.value}))} rows={3} style={{ ...inp, resize:'vertical' }} placeholder="Observações adicionais sobre o estado do veículo..." />
          </div>
          <div style={{ marginBottom:20 }}>
            <label style={lbl}>Assinatura do cliente (nome)</label>
            <input value={checklist.customer_signature} onChange={e=>setChecklist(c=>({...c,customer_signature:e.target.value}))} style={inp} placeholder="Nome do responsável que assinou" />
          </div>
          <button onClick={saveChecklist} style={{ padding:'10px 24px', background:'#2563EB', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer' }}>Salvar Checklist</button>
        </div>
      )}

      {!isNew && activeTab === 'photos' && (
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:24 }}>
          <h3 style={{ fontWeight:700, marginBottom:16, color:'#0F172A' }}>Fotos da O.S.</h3>
          <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:20 }}>
            <input type="file" accept="image/*" onChange={e=>setPhotoFile(e.target.files[0])} style={{ flex:1 }} />
            <button onClick={uploadPhoto} disabled={!photoFile} style={{ padding:'10px 20px', background:'#2563EB', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor: photoFile?'pointer':'not-allowed', opacity:photoFile?1:0.6 }}>Enviar foto</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12 }}>
            {(order?.photos || []).map(p => (
              <img key={p.id} src={p.url} alt="Foto OS" style={{ width:'100%', aspectRatio:'1', objectFit:'cover', borderRadius:10, border:'1px solid #E5E7EB' }} />
            ))}
            {(!order?.photos || order.photos.length === 0) && <p style={{ color:'#9CA3AF', gridColumn:'1/-1', textAlign:'center', padding:20 }}>Nenhuma foto enviada.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
